import * as fs from "fs";
import YAML from "yaml";
import chalk from "chalk";
import { PulseProtocol } from "../core/client.js";
import type { PulseConfig, Verdict, ScanInput } from "../core/types.js";

export interface DaemonOptions {
  config: PulseConfig;
  watchlistPath: string;
  interval: string;
  webhook?: string;
}

export interface WatchlistEntry extends ScanInput {
  label?: string;
}

/**
 * Run the Pulse Protocol daemon. Reads a YAML watchlist, scans every
 * entry on the given interval, and emits a webhook payload when a
 * verdict changes.
 */
export async function runDaemon(opts: DaemonOptions): Promise<void> {
  const intervalMs = parseDuration(opts.interval);
  const pulse = new PulseProtocol(opts.config);
  const previous = new Map<string, Verdict>();

  console.log(
    chalk.magenta(`pulse daemon started · interval ${opts.interval}`)
  );

  const tick = async () => {
    const raw = fs.readFileSync(opts.watchlistPath, "utf8");
    const parsed = YAML.parse(raw) as { targets: WatchlistEntry[] };
    const targets = parsed.targets ?? [];

    for (const target of targets) {
      try {
        const verdict = await pulse.scan(target);
        const key = `${target.handle}:${target.wallet}`;
        const prev = previous.get(key);

        if (!prev || prev.verdict !== verdict.verdict) {
          console.log(
            chalk.magenta(
              `[${new Date().toISOString()}] @${target.handle}: ${prev?.verdict ?? "NEW"} → ${verdict.verdict}`
            )
          );
          if (opts.webhook) {
            await emitWebhook(opts.webhook, { previous: prev, current: verdict });
          }
        } else {
          console.log(
            chalk.gray(
              `[${new Date().toISOString()}] @${target.handle}: ${verdict.verdict} (unchanged)`
            )
          );
        }

        previous.set(key, verdict);
      } catch (err) {
        console.error(
          chalk.red(`scan failed for @${target.handle}: ${(err as Error).message}`)
        );
      }
    }
  };

  await tick();
  setInterval(tick, intervalMs);
}

export class PulseDaemon {
  constructor(private readonly opts: DaemonOptions) {}
  async start(): Promise<void> {
    return runDaemon(this.opts);
  }
}

async function emitWebhook(
  url: string,
  payload: { previous?: Verdict; current: Verdict }
): Promise<void> {
  try {
    await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
  } catch (err) {
    console.error(chalk.red(`webhook failed: ${(err as Error).message}`));
  }
}

function parseDuration(s: string): number {
  const match = s.match(/^(\d+)(s|m|h|d)$/);
  if (!match) throw new Error(`invalid duration: ${s}`);
  const n = Number(match[1]);
  const unit = match[2];
  switch (unit) {
    case "s":
      return n * 1000;
    case "m":
      return n * 60_000;
    case "h":
      return n * 3_600_000;
    case "d":
      return n * 86_400_000;
    default:
      throw new Error(`invalid unit: ${unit}`);
  }
}
